import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';

// BPO RH — Agente RH: chat de consulta em linguagem natural sobre férias, prazos,
// admissões, rescisões, colaboradores e VA / VT / home office dos clientes da
// carteira. Só lê: o backend consulta as telas dos módulos e a IA redige. A
// conversa vive só no estado do React. Layout de tela cheia no estilo do
// Memor.IA Intelligence (01/10/2026), com a paleta do Saber (navy/ciano/dourado).
const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const NAVY = '#00205b';
const NAVY_2 = '#003580';
const CIANO = '#00b5cc';
const CIANO_ESCURO = '#008fa1';
const DOURADO = '#f1be48';
const FUNDO = '#f0f2f8';
const BORDA = '#e4e7f0';

// Anexos: mesmos limites do backend (3 arquivos de até 10 MB na mesma rota, via multipart).
const ANEXO_MAX = 3;
const ANEXO_MAX_MB = 10;
const ANEXO_EXTENSOES = ['.jpg', '.jpeg', '.png', '.webp', '.pdf', '.xlsx', '.xls', '.csv', '.docx', '.txt'];
const AVISO_SAUDE = 'Não anexe documentos de saúde (atestados, laudos).';

// Sugestões montadas com um cliente da carteira de quem está logado: exemplo fixo
// com nome de cliente vazaria a carteira para quem não a atende (regra da organização).
const sugestoes = (nome) => [
  { icone: 'fa-umbrella-beach', texto: `Quantas férias vencem este mês na ${nome}?` },
  { icone: 'fa-triangle-exclamation', texto: `O que está atrasado hoje na ${nome}?` },
  { icone: 'fa-scale-balanced', texto: 'Qual o prazo para pagar a rescisão?' },
  { icone: 'fa-utensils', texto: `O VA da ${nome} deste mês já foi pago?` },
];

// Negrito (**texto**) e marcadores ("- " / "• "), como no Assistente Financeiro.
const comNegrito = (txt) => txt.split(/\*\*(.*?)\*\*/g).map((p, j) =>
  (j % 2 === 1 ? <strong key={j} style={{ color: NAVY }}>{p}</strong> : p));

function renderMarkdown(texto) {
  return String(texto || '').split('\n').map((linha, i) => {
    if (/^\s*[-•]\s/.test(linha)) {
      return (
        <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 3 }}>
          <span style={{ color: CIANO_ESCURO, flexShrink: 0 }}>•</span>
          <span>{comNegrito(linha.replace(/^\s*[-•]\s*/, ''))}</span>
        </div>
      );
    }
    if (!linha.trim()) return <div key={i} style={{ height: 6 }} />;
    return <div key={i} style={{ marginBottom: 2 }}>{comNegrito(linha)}</div>;
  });
}

const celula = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));
const hora = (d) => (d ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '');

const tamanhoLegivel = (bytes) => (bytes >= 1048576
  ? `${(bytes / 1048576).toFixed(1).replace('.', ',')} MB`
  : `${Math.max(1, Math.round(bytes / 1024))} KB`);

const iconeAnexo = (nome) => {
  const ext = String(nome || '').slice(String(nome || '').lastIndexOf('.') + 1).toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp'].includes(ext)) return 'fa-image';
  if (ext === 'pdf') return 'fa-file-pdf';
  if (['xlsx', 'xls', 'csv'].includes(ext)) return 'fa-file-excel';
  if (ext === 'docx') return 'fa-file-word';
  return 'fa-file-lines';
};

// Trunca no meio para a extensão continuar visível (~28 caracteres).
const nomeCurto = (nome) => (nome.length <= 28 ? nome : `${nome.slice(0, 18)}…${nome.slice(-9)}`);

// Fichinha de anexo: na caixa de pergunta (com ✕) e no balão enviado (escura, sem ✕).
const FichaAnexo = ({ nome, tamanho, escura = false, aoRemover = null, desabilitado = false }) => (
  <span className={`arh-anexo ${escura ? 'arh-anexo-escura' : ''}`} title={nome}>
    <i className={`fas ${iconeAnexo(nome)}`} aria-hidden="true"></i>
    <span className="arh-anexo-nome">{nomeCurto(nome)}</span>
    <span className="arh-anexo-tam">{tamanhoLegivel(tamanho)}</span>
    {aoRemover && (
      <button type="button" aria-label={`Remover ${nome}`} disabled={desabilitado} onClick={aoRemover}>✕</button>
    )}
  </span>
);

// Nina: avatar feminino (atendente de headset) nas cores da casa — pedido de 08/10/2026
const NinaSvg = ({ tamanho = 24 }) => (
  <svg width={tamanho} height={tamanho} viewBox="0 0 64 64" aria-hidden="true">
    <path d="M32 7C19 7 10 16.5 10 29v14c0 4 2.5 6.5 6.5 6.5H21V31c0-7.5 5-12 11-12s11 4.5 11 12v18.5h4.5c4 0 6.5-2.5 6.5-6.5V29C54 16.5 45 7 32 7z" fill={NAVY} />
    <circle cx="32" cy="35" r="13" fill="#ffe3d2" />
    <path d="M20 33c.6-8.5 6.2-12.5 12-12.5S43.4 24.5 44 33c-3.2-4.8-7.6-6.5-12-6.5S23.2 28.2 20 33z" fill={NAVY} />
    <circle cx="26.6" cy="35.5" r="1.9" fill="#1a1d2e" />
    <circle cx="37.4" cy="35.5" r="1.9" fill="#1a1d2e" />
    <path d="M27.2 41c1.5 1.9 3.2 2.8 4.8 2.8s3.3-.9 4.8-2.8" stroke="#1a1d2e" strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <circle cx="18.3" cy="40.5" r="2.1" fill={DOURADO} />
    <circle cx="45.7" cy="40.5" r="2.1" fill={DOURADO} />
    <path d="M15 29c0-10.3 7.6-17.5 17-17.5S49 18.7 49 29" stroke={CIANO} strokeWidth="3.2" fill="none" strokeLinecap="round" />
    <rect x="12" y="27" width="5.4" height="11" rx="2.7" fill={CIANO} />
    <rect x="46.6" y="27" width="5.4" height="11" rx="2.7" fill={CIANO} />
    <path d="M14.7 38.5c.8 5.5 5.8 8.3 11.3 8.8" stroke={CIANO} strokeWidth="2.4" fill="none" strokeLinecap="round" />
    <circle cx="28" cy="47.6" r="2.5" fill={CIANO} />
  </svg>
);

const Avatar = ({ tipo, tamanho = 36 }) => (
  <div className="arh-avatar" style={{
    width: tamanho, height: tamanho,
    background: tipo === 'user' ? NAVY : '#fff',
    boxShadow: tipo === 'user' ? '0 2px 8px rgba(0,32,91,.25)' : '0 2px 8px rgba(0,181,204,.35)',
  }}>
    {tipo === 'user'
      ? <i className="fas fa-user" aria-hidden="true" style={{ color: '#fff', fontSize: tamanho * 0.42 }}></i>
      : <NinaSvg tamanho={tamanho * 0.66} />}
  </div>
);

const BpoRhAgenteRh = () => {
  const [mensagens, setMensagens] = useState([]); // {role:'user'|'agente', texto, em, pergunta?, tabela?, cliente?, fonte?, opcoes?, erro?}
  const [pergunta, setPergunta] = useState('');
  const [anexos, setAnexos] = useState([]); // File[] — até 3 arquivos junto da próxima pergunta
  const [enviando, setEnviando] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [gestao, setGestao] = useState(false);
  const [carregandoCli, setCarregandoCli] = useState(false);
  const [painel, setPainel] = useState(false);   // gaveta "Clientes e apelidos"
  const [editando, setEditando] = useState(null); // {id, valor, salvando}
  const fimRef = useRef(null);
  const inputRef = useRef(null);
  const arquivoRef = useRef(null);

  useEffect(() => { fimRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [mensagens, enviando]);

  // a caixa de texto cresce com o conteúdo (até ~6 linhas), como no ChatGPT
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [pergunta]);

  const carregarClientes = useCallback(() => {
    setCarregandoCli(true);
    axios.get(`${API}/api/bporh/agente/clientes/`, { params: { _: Date.now() }, headers: headers() })
      .then((r) => { setClientes(r.data.clientes || []); setGestao(!!r.data.gestao); })
      .catch((e) => notificar(e.response?.data?.mensagem || 'Não foi possível carregar os clientes'))
      .finally(() => setCarregandoCli(false));
  }, []);
  useEffect(() => { carregarClientes(); }, [carregarClientes]);

  // Últimos 6 pares pergunta → resposta (sem erros nem pedidos de escolha de cliente).
  const montarHistorico = (msgs) => msgs
    .filter((m) => m.role === 'agente' && !m.erro && m.pergunta && !m.opcoes?.length)
    .map((m) => ({ pergunta: m.pergunta, resposta: m.texto || '', cliente: m.cliente?.cliente || null }))
    .slice(-6);

  // Escolher arquivos acrescenta aos já selecionados (não substitui), respeitando o teto.
  const escolherAnexos = (e) => {
    const escolhidos = Array.from(e.target.files || []);
    e.target.value = ''; // permite escolher o mesmo arquivo de novo depois de remover
    if (!escolhidos.length) return;
    const avisos = [];
    const validos = [];
    escolhidos.forEach((f) => {
      const ponto = f.name.lastIndexOf('.');
      const ext = ponto >= 0 ? f.name.slice(ponto).toLowerCase() : '';
      if (!ANEXO_EXTENSOES.includes(ext)) { avisos.push(`"${nomeCurto(f.name)}": tipo não aceito`); return; }
      if (f.size > ANEXO_MAX_MB * 1048576) { avisos.push(`"${nomeCurto(f.name)}": maior que ${ANEXO_MAX_MB} MB`); return; }
      validos.push(f);
    });
    const vagas = Math.max(ANEXO_MAX - anexos.length, 0);
    const entram = validos.slice(0, vagas);
    if (validos.length > entram.length) {
      avisos.push(`Máximo de ${ANEXO_MAX} arquivos por pergunta: ${validos.length - entram.length} ignorado(s)`);
    }
    if (entram.length) setAnexos((as) => [...as, ...entram]);
    if (avisos.length) notificar(`⚠️ ${avisos.join(' · ')}`);
  };

  const perguntar = (texto, clienteId) => {
    const q = String(texto || '').trim();
    if (!q || enviando) return;
    const historico = montarHistorico(mensagens);
    const arquivos = anexos;
    // Escolha de cliente reenvia a mesma pergunta: não repete a bolha do usuário.
    if (!clienteId) {
      setMensagens((ms) => [...ms, {
        role: 'user', texto: q, em: new Date(),
        anexos_info: arquivos.length ? arquivos.map((f) => ({ nome: f.name, tamanho: f.size })) : undefined,
      }]);
    }
    setPergunta('');
    setEnviando(true);
    // Com anexos a mesma rota recebe multipart/form-data (historico vira string JSON);
    // sem anexos, segue o POST JSON de sempre.
    let corpo;
    if (arquivos.length) {
      corpo = new FormData();
      corpo.append('pergunta', q);
      if (clienteId) corpo.append('cliente_id', clienteId);
      corpo.append('historico', JSON.stringify(historico));
      arquivos.forEach((f) => corpo.append('arquivos', f));
    } else {
      corpo = { pergunta: q, historico };
      if (clienteId) corpo.cliente_id = clienteId;
    }
    axios.post(`${API}/api/bporh/agente/`, corpo, { headers: headers() })
      .then((r) => {
        const d = r.data || {};
        if (!d.success) {
          // anexos ficam na caixa para a pessoa corrigir e reenviar
          setMensagens((ms) => [...ms, { role: 'agente', erro: true, em: new Date(), texto: d.mensagem || 'Não foi possível responder' }]);
          return;
        }
        if (arquivos.length) setAnexos([]);
        setMensagens((ms) => [...ms, {
          role: 'agente', pergunta: q, texto: d.texto || '', tabela: d.tabela || null, em: new Date(),
          cliente: d.cliente || null, fonte: d.fonte || null, opcoes: d.pergunta_cliente || null,
        }]);
      })
      .catch((e) => {
        const msg = e.response?.data?.mensagem || 'Não foi possível consultar o agente. Tente de novo em instantes.';
        setMensagens((ms) => [...ms, { role: 'agente', erro: true, em: new Date(), texto: msg }]);
      })
      .finally(() => {
        setEnviando(false);
        setTimeout(() => inputRef.current?.focus(), 0);
      });
  };

  const aoTeclar = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); perguntar(pergunta); }
  };

  const salvarApelidos = () => {
    if (!editando) return;
    setEditando((s) => ({ ...s, salvando: true }));
    axios.put(`${API}/api/bporh/agente/clientes/`, { cliente_id: editando.id, apelidos: editando.valor }, { headers: headers() })
      .then((r) => {
        setClientes((cs) => cs.map((c) => (c.id === editando.id ? { ...c, apelidos: r.data.apelidos } : c)));
        notificar(`✅ ${r.data.mensagem || 'Apelidos salvos'}`);
        setEditando(null);
      })
      .catch((e) => {
        notificar(`❌ ${e.response?.data?.mensagem || 'Não foi possível salvar os apelidos'}`);
        setEditando((s) => (s ? { ...s, salvando: false } : s));
      });
  };

  const temConversa = mensagens.length > 0 || enviando;
  const podeEnviar = !!pergunta.trim() && !enviando;

  const renderMensagem = (m, i) => {
    const eu = m.role === 'user';
    return (
      <div key={i} className={`arh-linha ${eu ? 'arh-linha-eu' : ''}`}>
        {!eu && <Avatar tipo="agente" />}
        <div className="arh-coluna" style={{ alignItems: eu ? 'flex-end' : 'flex-start' }}>
          {!eu && (m.cliente || m.fonte) && (
            <div className="arh-selo">
              {m.cliente?.cliente && <span><i className="fas fa-building me-1" aria-hidden="true"></i>{m.cliente.cliente}</span>}
              {m.fonte && <span><i className="fas fa-table-list me-1" aria-hidden="true"></i>{m.fonte}</span>}
            </div>
          )}
          <div className={`arh-bolha ${eu ? 'arh-bolha-eu' : m.erro ? 'arh-bolha-erro' : 'arh-bolha-agente'}`}>
            {eu ? m.texto : m.erro
              ? <><i className="fas fa-circle-exclamation me-2" aria-hidden="true"></i>{m.texto}</>
              : renderMarkdown(m.texto)}
            {eu && m.anexos_info?.length > 0 && (
              <div className="arh-bolha-anexos">
                {m.anexos_info.map((a, j) => <FichaAnexo key={j} nome={a.nome} tamanho={a.tamanho} escura />)}
              </div>
            )}
            {m.opcoes?.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {m.opcoes.map((op) => (
                  <button key={op.id} type="button" className="arh-chip" disabled={enviando || m.resolvida}
                    onClick={() => {
                      // escolhido o cliente, as opções desta mensagem ficam travadas
                      setMensagens((ms) => ms.map((x) => (x === m ? { ...x, resolvida: true } : x)));
                      perguntar(m.pergunta, op.id);
                    }}>
                    <i className="fas fa-building me-1" aria-hidden="true"></i>{op.cliente}
                  </button>
                ))}
              </div>
            )}
            {m.tabela?.colunas?.length > 0 && (
              <div className="arh-tabela">
                <table className="table table-sm small mb-0">
                  <thead>
                    <tr>{m.tabela.colunas.map((c, j) => <th key={j} className="text-nowrap">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(m.tabela.linhas || []).map((l, j) => (
                      <tr key={j}>{l.map((val, k) => <td key={k}>{celula(val)}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <small className="arh-hora">{hora(m.em)}</small>
        </div>
        {eu && <Avatar tipo="user" />}
      </div>
    );
  };

  return (
    <div className="arh-tela">
      {/* ── Barra superior ── */}
      <div className="arh-topo">
        <div className="d-flex align-items-center gap-3">
          <Avatar tipo="agente" tamanho={40} />
          <div>
            <div className="d-flex align-items-center gap-2">
              <span className="arh-titulo">Agente RH</span>
              <span className="arh-beta">BETA</span>
            </div>
            <div className="arh-subtitulo">BPO RH · só consulta, nada é alterado</div>
          </div>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="arh-btn-claro" onClick={() => setPainel(true)}>
            <i className="fas fa-tags me-1" aria-hidden="true" style={{ color: DOURADO }}></i>Clientes e apelidos
          </button>
          {mensagens.length > 0 && (
            <button type="button" className="arh-btn-claro" onClick={() => setMensagens([])} disabled={enviando}>
              <i className="fas fa-pen-to-square me-1" aria-hidden="true"></i>Nova conversa
            </button>
          )}
        </div>
      </div>

      {/* ── Mensagens ── */}
      <div className="arh-rolagem">
        {!temConversa ? (
          <div className="arh-vazio">
            <div className="arh-vazio-avatar"><NinaSvg tamanho={56} /></div>
            <h4 className="arh-vazio-titulo">Como posso ajudar?</h4>
            <p className="arh-vazio-texto">
              Pergunte sobre férias, prazos, admissões, rescisões, colaboradores e VA / VT / home office de qualquer
              cliente da sua carteira, ou tire dúvidas de legislação trabalhista e da convenção coletiva do cliente.
            </p>
            <div className="arh-sugestoes">
              {sugestoes(clientes[0]?.cliente || 'empresa X').map((s) => (
                <button key={s.texto} type="button" className="arh-sugestao" onClick={() => perguntar(s.texto)}>
                  <span className="arh-sugestao-icone"><i className={`fas ${s.icone}`} aria-hidden="true"></i></span>
                  <span>{s.texto}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="arh-conversa">
            {mensagens.map(renderMensagem)}
            {enviando && (
              <div className="arh-linha">
                <Avatar tipo="agente" />
                <div className="arh-bolha arh-bolha-agente d-flex align-items-center gap-2">
                  <span className="arh-digitando"><i></i><i></i><i></i></span>
                  <span className="text-muted small">consultando os dados…</span>
                </div>
              </div>
            )}
            <div ref={fimRef} />
          </div>
        )}
      </div>

      {/* ── Caixa de pergunta ── */}
      <div className="arh-rodape">
        <div className="arh-caixa">
          {anexos.length > 0 && (
            <div className="arh-anexos">
              {anexos.map((f, i) => (
                <FichaAnexo key={`${f.name}-${i}`} nome={f.name} tamanho={f.size} desabilitado={enviando}
                  aoRemover={() => setAnexos((as) => as.filter((_, j) => j !== i))} />
              ))}
              <small className="arh-anexo-aviso">
                <i className="fas fa-shield-halved me-1" aria-hidden="true"></i>{AVISO_SAUDE}
              </small>
            </div>
          )}
          <div className="d-flex align-items-end gap-2">
            <button type="button" className="arh-clipe" disabled={enviando} title={AVISO_SAUDE}
              aria-label="Anexar arquivos" onClick={() => arquivoRef.current?.click()}>
              <i className="fas fa-paperclip" aria-hidden="true"></i>
            </button>
            <input ref={arquivoRef} type="file" hidden multiple
              accept=".jpg,.jpeg,.png,.webp,.pdf,.xlsx,.xls,.csv,.docx,.txt" onChange={escolherAnexos} />
            <textarea ref={inputRef} rows={1} maxLength={1000} value={pergunta} disabled={enviando}
              placeholder="Pergunte ao Agente RH… (ex.: férias vencendo na empresa X este mês)"
              aria-label="Pergunta ao Agente RH"
              onChange={(e) => setPergunta(e.target.value)} onKeyDown={aoTeclar} />
            <button type="button" className="arh-enviar" disabled={!podeEnviar} onClick={() => perguntar(pergunta)}
              aria-label="Enviar pergunta"
              style={{
                background: podeEnviar ? `linear-gradient(135deg, ${NAVY}, ${NAVY_2})` : '#eef0f8',
                color: podeEnviar ? '#fff' : '#b0b7cc',
                boxShadow: podeEnviar ? '0 4px 14px rgba(0,32,91,.4)' : 'none',
              }}>
              <i className={`fas ${enviando ? 'fa-spinner fa-spin' : 'fa-arrow-up'}`} aria-hidden="true"></i>
            </button>
          </div>
          <div className="arh-dicas">
            <small>Enter para enviar · Shift+Enter para nova linha</small>
            <small><i className="fas fa-shield-halved me-1" aria-hidden="true" style={{ color: CIANO }}></i>Só consulta · CPF mascarado na IA</small>
          </div>
        </div>
      </div>

      {/* ── Gaveta: clientes e apelidos ── */}
      {painel && (
        <div className="arh-gaveta-fundo" onClick={(e) => { if (e.target === e.currentTarget) setPainel(false); }}>
          <aside className="arh-gaveta" aria-label="Clientes e apelidos">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="m-0 fw-bold" style={{ color: '#212529' }}>
                <i className="fas fa-tags me-2" aria-hidden="true" style={{ color: DOURADO }}></i>Clientes e apelidos
                <span className="badge bg-light text-dark ms-2">{clientes.length}</span>
              </h6>
              <button type="button" className="btn-close" aria-label="Fechar" onClick={() => setPainel(false)}></button>
            </div>
            <div className="small text-muted mb-3">
              É por estes nomes (e apelidos) que o agente entende o cliente citado na pergunta.
              {gestao ? ' Clique no lápis para editar.' : ' A gestão edita.'}
            </div>
            {carregandoCli && clientes.length === 0 ? (
              <div className="text-center py-3"><i className="fas fa-spinner fa-spin" style={{ color: CIANO_ESCURO }} aria-hidden="true"></i></div>
            ) : (
              <div className="flex-grow-1 overflow-auto">
                {clientes.length === 0 && <div className="small text-muted">Nenhum cliente na sua carteira.</div>}
                {clientes.map((c) => (
                  <div key={c.id} className="py-2 border-bottom">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-semibold small">{c.cliente}</div>
                        {editando?.id !== c.id && <div className="small text-muted">{c.apelidos || '—'}</div>}
                      </div>
                      {gestao && editando?.id !== c.id && (
                        <button type="button" className="btn btn-sm btn-link p-0" title="Editar apelidos" aria-label={`Editar apelidos de ${c.cliente}`}
                          onClick={() => setEditando({ id: c.id, valor: c.apelidos || '', salvando: false })}>
                          <i className="fas fa-pencil" aria-hidden="true"></i>
                        </button>
                      )}
                    </div>
                    {editando?.id === c.id && (
                      <div className="d-flex gap-1 mt-1">
                        <input type="text" className="form-control form-control-sm" value={editando.valor} autoFocus
                          placeholder="ex.: acme, acme brasil" disabled={editando.salvando}
                          onChange={(e) => setEditando((s) => ({ ...s, valor: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') salvarApelidos(); if (e.key === 'Escape') setEditando(null); }} />
                        <button type="button" className="btn btn-sm text-white" style={{ background: NAVY }} onClick={salvarApelidos} disabled={editando.salvando}>
                          {editando.salvando ? <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> : 'Salvar'}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditando(null)} disabled={editando.salvando} aria-label="Cancelar">✕</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </aside>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        .arh-tela {
          position: fixed; top: 0; left: 6rem; right: 0; bottom: 0; z-index: 1;
          display: flex; flex-direction: column; overflow: hidden; background: ${FUNDO};
          font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif;
        }
        @media (max-width: 768px) { .arh-tela { left: 0; } }
        .arh-topo {
          flex-shrink: 0; display: flex; justify-content: space-between; align-items: center; gap: 12px;
          padding: 1rem 2rem; background: #fff; border-bottom: 1px solid ${BORDA}; box-shadow: 0 1px 6px rgba(0,0,0,.05);
        }
        @media (max-width: 768px) { .arh-topo { padding: .75rem 1rem 0.75rem 3.5rem; flex-wrap: wrap; } }
        .arh-titulo { font-weight: 800; font-size: 1.1rem; letter-spacing: -.3px; color: #1a1d2e; }
        .arh-subtitulo { font-size: .72rem; color: #7a8199; margin-top: 1px; }
        .arh-beta {
          background: linear-gradient(135deg, ${NAVY}, ${NAVY_2}); color: #fff; font-size: .6rem; font-weight: 700;
          padding: 2px 9px; border-radius: 20px; letter-spacing: .5px;
        }
        .arh-btn-claro {
          border-radius: 10px; font-size: .8rem; padding: .35rem .75rem; background: #f5f6fa; color: #495057;
          border: 1px solid ${BORDA}; transition: background .15s;
        }
        .arh-btn-claro:hover:not(:disabled) { background: #eceef5; }
        .arh-avatar { border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .arh-rolagem { flex: 1; overflow-y: auto; display: flex; flex-direction: column; }
        .arh-vazio {
          flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 2rem 1.5rem; text-align: center; max-width: 780px; width: 100%; margin: 0 auto;
        }
        .arh-vazio-avatar {
          width: 90px; height: 90px; border-radius: 50%; background: #fff; display: flex; align-items: center;
          justify-content: center; margin-bottom: 1.5rem; box-shadow: 0 8px 32px rgba(0,181,204,.35);
        }
        .arh-vazio-avatar i { color: ${CIANO_ESCURO}; font-size: 2.6rem; }
        .arh-vazio-titulo { font-weight: 800; margin: 0 0 .6rem; font-size: 1.55rem; color: #1a1d2e; letter-spacing: -.5px; }
        .arh-vazio-texto { color: #7a8199; font-size: 1rem; max-width: 520px; margin-bottom: 2.5rem; line-height: 1.6; }
        .arh-sugestoes { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; width: 100%; }
        @media (max-width: 640px) { .arh-sugestoes { grid-template-columns: 1fr; } }
        .arh-sugestao {
          background: #fff; border: 1.5px solid ${BORDA}; border-radius: 16px; padding: 1.1rem 1.25rem;
          font-size: .93rem; color: #3a3d52; text-align: left; display: flex; align-items: flex-start; gap: 12px;
          line-height: 1.5; transition: border-color .2s, box-shadow .2s, transform .15s;
        }
        .arh-sugestao:hover { border-color: ${CIANO}; box-shadow: 0 4px 16px rgba(0,181,204,.15); transform: translateY(-2px); }
        .arh-sugestao-icone {
          width: 36px; height: 36px; border-radius: 10px; flex-shrink: 0; background: rgba(0,181,204,.1);
          display: flex; align-items: center; justify-content: center; color: ${CIANO_ESCURO};
        }
        .arh-conversa { padding: 1.5rem; width: 100%; max-width: 940px; margin: 0 auto; }
        .arh-linha { display: flex; gap: 12px; margin-bottom: 1.1rem; align-items: flex-end; }
        .arh-linha-eu { justify-content: flex-end; }
        .arh-coluna { display: flex; flex-direction: column; max-width: 78%; min-width: 0; }
        @media (max-width: 640px) { .arh-coluna { max-width: 88%; } }
        .arh-selo { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 4px; }
        .arh-selo span {
          font-size: .68rem; font-weight: 600; color: ${CIANO_ESCURO}; background: rgba(0,181,204,.08);
          border: 1px solid rgba(0,181,204,.25); border-radius: 20px; padding: 1px 9px;
        }
        .arh-bolha {
          padding: .85rem 1.15rem; font-size: .95rem; line-height: 1.7; letter-spacing: -.01em;
          overflow-wrap: anywhere; min-width: 0; max-width: 100%;
        }
        .arh-bolha-eu {
          border-radius: 20px 20px 5px 20px; color: #fff; white-space: pre-wrap;
          background: linear-gradient(135deg, ${NAVY}, ${NAVY_2}); box-shadow: 0 4px 14px rgba(0,32,91,.3);
        }
        .arh-bolha-agente { border-radius: 20px 20px 20px 5px; background: #fff; color: #2d3047; box-shadow: 0 2px 8px rgba(0,0,0,.07); }
        .arh-bolha-erro { border-radius: 20px 20px 20px 5px; background: #fff3cd; color: #664d03; box-shadow: 0 2px 8px rgba(0,0,0,.05); }
        .arh-hora { font-size: .68rem; color: #9ca3af; margin-top: 4px; }
        .arh-chip {
          border: 1.5px solid ${CIANO}; color: ${CIANO_ESCURO}; background: #fff; border-radius: 20px;
          font-size: .8rem; padding: .25rem .8rem; transition: background .15s;
        }
        .arh-chip:hover:not(:disabled) { background: rgba(0,181,204,.08); }
        .arh-chip:disabled { opacity: .5; }
        .arh-tabela {
          margin-top: .75rem; max-height: 280px; overflow: auto; border: 1px solid ${BORDA}; border-radius: 12px; background: #fff;
        }
        .arh-tabela thead th {
          position: sticky; top: 0; background: #f7f8fc; color: #5a607a; font-weight: 600; font-size: .72rem;
          text-transform: uppercase; letter-spacing: .3px; border-bottom: 1px solid ${BORDA};
        }
        .arh-tabela td { color: #2d3047; border-color: #f0f2f8; }
        .arh-tabela tbody tr:hover td { background: #fafbfe; }
        .arh-digitando { display: inline-flex; gap: 4px; }
        .arh-digitando i {
          width: 7px; height: 7px; border-radius: 50%; background: ${CIANO}; display: block;
          animation: arhDigitando 1.2s ease-in-out infinite;
        }
        .arh-digitando i:nth-child(2) { animation-delay: .2s; }
        .arh-digitando i:nth-child(3) { animation-delay: .4s; }
        @keyframes arhDigitando { 0%, 60%, 100% { transform: translateY(0); opacity: .4; } 30% { transform: translateY(-6px); opacity: 1; } }
        .arh-rodape { flex-shrink: 0; padding: .75rem 2rem 1.5rem; background: ${FUNDO}; }
        @media (max-width: 768px) { .arh-rodape { padding: .5rem .75rem 1rem; } }
        .arh-caixa {
          max-width: 900px; margin: 0 auto; background: #fff; border: 1.5px solid ${BORDA}; border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0,0,0,.08); padding: .75rem .85rem .6rem 1.15rem; transition: border-color .2s;
        }
        .arh-caixa:focus-within { border-color: ${CIANO}; }
        .arh-caixa textarea {
          flex: 1; border: none; outline: none; resize: none; background: transparent; font-size: .97rem;
          line-height: 1.6; max-height: 160px; overflow-y: auto; padding: .35rem 0; color: #111827; font-family: inherit;
        }
        .arh-caixa textarea::placeholder { color: #9ca3af; }
        .arh-enviar {
          width: 40px; height: 40px; border-radius: 50%; border: none; flex-shrink: 0; display: flex;
          align-items: center; justify-content: center; transition: all .2s;
        }
        .arh-clipe {
          width: 40px; height: 40px; border-radius: 50%; border: none; flex-shrink: 0; display: flex;
          align-items: center; justify-content: center; background: #f5f6fa; color: #5a607a; transition: background .15s, color .15s;
        }
        .arh-clipe:hover:not(:disabled) { background: #eceef5; color: ${NAVY}; }
        .arh-clipe:disabled { opacity: .5; }
        .arh-anexos {
          display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
          margin-bottom: .5rem; padding-bottom: .5rem; border-bottom: 1px solid #f0f2f8;
        }
        .arh-anexo {
          display: inline-flex; align-items: center; gap: 6px; font-size: .75rem; color: #3a3d52;
          background: #f5f6fa; border: 1px solid ${BORDA}; border-radius: 20px; padding: 3px 10px; max-width: 100%;
        }
        .arh-anexo i { color: ${CIANO_ESCURO}; }
        .arh-anexo-nome { font-weight: 600; white-space: nowrap; }
        .arh-anexo-tam { color: #9ca3af; white-space: nowrap; }
        .arh-anexo button { border: none; background: transparent; color: #9ca3af; padding: 0 2px; font-size: .75rem; line-height: 1; }
        .arh-anexo button:hover:not(:disabled) { color: #dc3545; }
        .arh-anexo button:disabled { opacity: .5; }
        .arh-anexo-escura { background: rgba(255,255,255,.14); border-color: rgba(255,255,255,.3); color: #fff; }
        .arh-anexo-escura i { color: ${DOURADO}; }
        .arh-anexo-escura .arh-anexo-tam { color: rgba(255,255,255,.7); }
        .arh-anexo-aviso { color: #b0b7cc; font-size: .72rem; }
        .arh-anexo-aviso i { color: ${DOURADO}; }
        .arh-bolha-anexos { display: flex; flex-wrap: wrap; gap: 6px; margin-top: .5rem; }
        .arh-dicas {
          display: flex; justify-content: space-between; gap: 8px; flex-wrap: wrap; margin-top: .5rem;
          padding-top: .5rem; border-top: 1px solid #f0f2f8;
        }
        .arh-dicas small { color: #b0b7cc; font-size: .72rem; }
        .arh-gaveta-fundo { position: fixed; inset: 0; background: rgba(15,23,42,.35); z-index: 1060; display: flex; justify-content: flex-end; }
        .arh-gaveta {
          width: 380px; max-width: 100%; height: 100%; background: #fff; padding: 1.25rem; display: flex;
          flex-direction: column; box-shadow: -6px 0 24px rgba(0,0,0,.12);
        }
        .arh-rolagem::-webkit-scrollbar, .arh-tabela::-webkit-scrollbar { width: 6px; height: 6px; }
        .arh-rolagem::-webkit-scrollbar-thumb, .arh-tabela::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default BpoRhAgenteRh;
